import { useState } from 'react';
import { useTreasuryYields } from '@/hooks/useTreasuryYields';
import { useInflation } from '@/hooks/useInflation';
import useEquityBenchmark from '@/hooks/useEquityBenchmark';
import AbbrTip from '@/components/AbbrTip';
import { computeRealYield } from '@/lib/realYield';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/**
 * MacroYieldStrip — Phase 6b (Jun 2026)
 *
 * Live U.S. Treasury yield curve mini-strip + portfolio-vs-10Y
 * spread. Sits directly under the Forward/Year-3/Year-5 income
 * row inside Portfolio Summary so dividend investors can instantly
 * see the risk-free rate they're competing against.
 *
 * Reads from /api/macro/treasury-yields (populated nightly by the
 * `treasury-yields` cron). Hides itself when the cache is empty.
 *
 * Layout (Round 95ab — Jun 2026, per user spec):
 *   • Left-aligned (not centered) so the strip lines up with the
 *     rest of the card's left rail.
 *   • Each pill renders its full explanation text on a line directly
 *     below the pill, in the SAME font/size as the strip itself — no
 *     hover tooltips, no `title=` attributes. Inverted curve and
 *     spread interpretation are always visible at a glance.
 */
export default function MacroYieldStrip({ portfolioYieldPct }) {
  const curve = useTreasuryYields();
  const inflation = useInflation();
  const equityBench = useEquityBenchmark();
  const [infoModal, setInfoModal] = useState(null); // 'realYield' | 'erp' | null
  if (!curve) return null;

  const tenors = [
    { key: 'yield_3_month',  label: '3M' },
    { key: 'yield_2_year',   label: '2Y' },
    { key: 'yield_10_year',  label: '10Y' },
    { key: 'yield_30_year',  label: '30Y' },
  ];
  const cells = tenors
    .map((t) => ({ ...t, value: curve[t.key] }))
    .filter((t) => t.value != null);
  if (cells.length === 0) return null;

  const ten = curve.yield_10_year;
  const two = curve.yield_2_year;
  const inverted = ten != null && two != null && two > ten;

  const showPortfolioSpread = (
    typeof portfolioYieldPct === 'number'
    && Number.isFinite(portfolioYieldPct)
    && ten != null
  );
  const spread = showPortfolioSpread ? (portfolioYieldPct - ten) : null;
  const isProfit = spread != null && spread >= 0;

  // Phase 7 (Jun 2026, Fisher-updated Jul 7, 2026) — Real Yield uses
  // the exact multiplicative Fisher equation (1 + nominal)/(1 + CPI) − 1
  // instead of the linear nominal − CPI approximation. Same idea,
  // more accurate at higher yields.
  const cpiYoY = inflation?.cpi_yoy;
  const hasInflation = (
    typeof portfolioYieldPct === 'number'
    && Number.isFinite(portfolioYieldPct)
    && typeof cpiYoY === 'number'
    && Number.isFinite(cpiYoY)
  );
  const realYield = hasInflation ? computeRealYield(portfolioYieldPct, cpiYoY) : null;
  const realIsProfit = realYield != null && realYield >= 0;

  // Sep 2026 — Equity Risk Premium (moved here from the retired Income
  // by Portfolio card). Same math as before: real yield minus the
  // Damodaran long-run T-Bond real return (HIST) and minus today's
  // Fisher-exact 10Y real yield (NOW).
  const tbondReal = equityBench?.historical_real_tbond_pct;
  const tbondRealNow = equityBench?.current_real_tbond_pct;
  const showErpHist = hasInflation && typeof tbondReal === 'number';
  const showErpNow = hasInflation && typeof tbondRealNow === 'number';
  const erpHist = showErpHist ? realYield - tbondReal : null;
  const erpNow = showErpNow ? realYield - tbondRealNow : null;
  const fmtPp = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}pp`;
  const realYieldModalText = hasInflation
    ? `Real Yield (Fisher-exact) = (1 + Nominal ${portfolioYieldPct.toFixed(2)}%) / (1 + CPI YoY ${cpiYoY.toFixed(2)}%) − 1 = ${realYield.toFixed(2)}%. Positive means this portfolio's income is beating inflation; negative means purchasing power is shrinking.`
    : '';
  const erpModalText = (
    `Equity Risk Premium (ERP) — how much your dividend income beats the "risk-free" real return.\n\n` +
    (showErpHist ? `HIST: your ${realYield.toFixed(2)}% real − Damodaran long-run T-Bond real ${tbondReal.toFixed(2)}% (${equityBench?.period || '—'}) = ${fmtPp(erpHist)}.\n` : '') +
    (showErpNow ? `NOW: your ${realYield.toFixed(2)}% real − today's Fisher-exact 10Y real ${tbondRealNow.toFixed(2)}% = ${fmtPp(erpNow)}.\n\n` : '\n') +
    `Positive means your income is earning a genuine premium over safe long-term bonds; negative means you're taking equity risk without being paid extra for it.`
  );

  const curveCaption = ten != null
    ? `U.S. Treasury yield curve, last updated ${curve.date}. Source: Federal Reserve via Massive. Every dividend stock implicitly competes with these risk-free rates — when the 10Y yields ${ten.toFixed(2)}% you're getting paid that much for taking ZERO equity risk.`
    : null;

  // Phase 6b followup (Jun 2026) — spread caption rendered as JSX so
  // we can inline an AbbrTip popover on the "(pp)" abbreviation. The
  // popover links to the new Glossary entry for Percentage Points so
  // users can verify the difference between a pp move and a % move.
  const renderSpreadCaption = () => {
    if (!showPortfolioSpread) return null;
    const absSpread = Math.abs(spread).toFixed(2);
    const pp = <AbbrTip term="PP">(pp)</AbbrTip>;
    if (isProfit) {
      return (
        <>
          Your portfolio yields {portfolioYieldPct.toFixed(2)}% vs the 10Y Treasury at {ten.toFixed(2)}%.
          {' '}You&rsquo;re earning +{absSpread} percentage points {pp} OVER the risk-free rate — fair compensation for taking equity risk.
        </>
      );
    }
    return (
      <>
        Your portfolio yields {portfolioYieldPct.toFixed(2)}% vs the 10Y Treasury at {ten.toFixed(2)}%.
        {' '}Your portfolio yields {absSpread} percentage points {pp} BELOW the risk-free rate — you&rsquo;re currently taking equity risk for less income than a 10Y bond pays.
      </>
    );
  };

  return (
    <div className="mt-2 px-2 sm:px-3" data-testid="macro-yield-strip">
      <div className="flex flex-col items-start gap-2 text-[10px] sm:text-[11px]">
        {/* Treasury curve pill + its caption line */}
        <div className="flex flex-col items-start gap-1 w-full">
          <div
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/20"
            data-testid="macro-yield-curve-pill"
          >
            <span className="text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wide text-[9px]">
              US Treasury
            </span>
            {cells.map((c, i) => (
              <span key={c.key} className="inline-flex items-center gap-1">
                {i > 0 && <span className="text-muted-foreground/60">·</span>}
                <span className="text-muted-foreground">{c.label}</span>
                <span className="font-mono font-bold text-foreground">
                  {c.value.toFixed(2)}%
                </span>
              </span>
            ))}
            {inverted && (
              <span className="ml-1 inline-flex items-center px-1.5 py-px rounded-sm text-[9px] font-bold uppercase bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                Inverted
              </span>
            )}
          </div>
          {curveCaption && (
            <p
              className="text-muted-foreground leading-snug w-full"
              data-testid="macro-yield-curve-caption"
            >
              {curveCaption}
              {inverted && (
                <span className="ml-1 text-rose-700 dark:text-rose-400 font-semibold">
                  2Y &gt; 10Y — inverted curve, historically a recession leading indicator.
                </span>
              )}
            </p>
          )}
        </div>

        {/* Portfolio vs 10Y pill + its caption line */}
        {showPortfolioSpread && (
          <div className="flex flex-col items-start gap-1 w-full">
            <div
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed ${
                isProfit
                  ? 'border-emerald-400/50 bg-emerald-50/40 dark:bg-emerald-950/20'
                  : 'border-rose-400/50 bg-rose-50/40 dark:bg-rose-950/20'
              }`}
              data-testid="macro-portfolio-spread-pill"
            >
              <span className="text-muted-foreground">Portfolio vs 10Y</span>
              <span
                className={`font-mono font-bold ${
                  isProfit
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-rose-700 dark:text-rose-400'
                }`}
              >
                {isProfit ? '+' : ''}{spread.toFixed(2)}pp
              </span>
            </div>
            <p
              className="text-muted-foreground leading-snug w-full"
              data-testid="macro-portfolio-spread-caption"
            >
              {renderSpreadCaption()}
            </p>
          </div>
        )}

        {/* Phase 7 (Jun 2026, Fisher-updated Jul 7, 2026) — Real
            Yield pill. Uses the multiplicative Fisher equation
            (1 + portfolio yield)/(1 + CPI YoY) − 1 to tell the user
            whether their dividend stream is actually growing
            purchasing power. */}
        {hasInflation && (
          <div className="flex flex-col items-start gap-1 w-full">
            <button
              type="button"
              onClick={() => setInfoModal('realYield')}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed cursor-pointer hover:opacity-80 transition-opacity ${
                realIsProfit
                  ? 'border-emerald-400/50 bg-emerald-50/40 dark:bg-emerald-950/20'
                  : 'border-rose-400/50 bg-rose-50/40 dark:bg-rose-950/20'
              }`}
              data-testid="macro-real-yield-pill"
              title="Tap for the Fisher-exact formula"
            >
              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">Real Yield</span>
              <span
                className={`font-mono font-bold ${
                  realIsProfit
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-rose-700 dark:text-rose-400'
                }`}
              >
                {realIsProfit ? '+' : ''}{realYield.toFixed(2)}%
              </span>
              <span className="text-muted-foreground/70">·</span>
              <span className="text-muted-foreground"><AbbrTip term="CPI">CPI</AbbrTip></span>
              <span className="font-mono font-bold text-foreground">{cpiYoY.toFixed(2)}%</span>
            </button>
            <p
              className="text-muted-foreground leading-snug w-full"
              data-testid="macro-real-yield-caption"
            >
              Your portfolio yields {portfolioYieldPct.toFixed(2)}% while <AbbrTip term="CPI">CPI</AbbrTip> ran at {cpiYoY.toFixed(2)}% over the last 12 months.
              {' '}{realIsProfit
                ? <>You&rsquo;re earning +{realYield.toFixed(2)} percentage points <AbbrTip term="PP">(pp)</AbbrTip> ABOVE inflation — your dividend stream is genuinely growing your purchasing power, not just keeping pace.</>
                : <>Your dividend stream is running {Math.abs(realYield).toFixed(2)} percentage points <AbbrTip term="PP">(pp)</AbbrTip> BELOW inflation — purchasing power is eroding even though the cash payouts feel positive.</>}
            </p>
          </div>
        )}

        {/* Sep 2026 — Equity Risk Premium pill (HIST · NOW), tap for
            the full breakdown. */}
        {(showErpHist || showErpNow) && (
          <div className="flex flex-col items-start gap-1 w-full">
            <button
              type="button"
              onClick={() => setInfoModal('erp')}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed border-sky-400/50 bg-sky-50/40 dark:bg-sky-950/20 cursor-pointer hover:opacity-80 transition-opacity"
              data-testid="macro-erp-pill"
              title="Tap for the Equity Risk Premium breakdown"
            >
              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">ERP</span>
              {showErpHist && (
                <span className={`font-mono font-bold ${erpHist >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`} data-testid="macro-erp-hist">
                  {fmtPp(erpHist)} <span className="font-sans font-normal text-muted-foreground">hist</span>
                </span>
              )}
              {showErpHist && showErpNow && <span className="text-muted-foreground/70">·</span>}
              {showErpNow && (
                <span className={`font-mono font-bold ${erpNow >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`} data-testid="macro-erp-now">
                  {fmtPp(erpNow)} <span className="font-sans font-normal text-muted-foreground">now</span>
                </span>
              )}
            </button>
            <p className="text-muted-foreground leading-snug w-full" data-testid="macro-erp-caption">
              Equity Risk Premium — how much your {realYield.toFixed(2)}% real yield beats the risk-free real return
              {showErpHist && <> of long-run Treasury bonds ({tbondReal.toFixed(2)}%, {fmtPp(erpHist)})</>}
              {showErpHist && showErpNow && ' and'}
              {showErpNow && <> today&rsquo;s 10Y Treasury ({tbondRealNow.toFixed(2)}% real, {fmtPp(erpNow)})</>}. Tap the pill for the full breakdown.
            </p>
          </div>
        )}
      </div>
      <Dialog open={infoModal !== null} onOpenChange={(o) => !o && setInfoModal(null)}>
        <DialogContent className="max-w-md" data-testid="macro-info-modal">
          <DialogHeader>
            <DialogTitle>
              {infoModal === 'realYield' && 'Real Yield (Fisher-exact)'}
              {infoModal === 'erp' && 'Equity Risk Premium (ERP)'}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">
            {infoModal === 'realYield' && realYieldModalText}
            {infoModal === 'erp' && erpModalText}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </div>
  );
}
